import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * Hook para Text-to-Speech usando Web Speech API
 * Suporta play, pause, stop, controle de velocidade e voz com chunking para maior estabilidade
 */
const useTextToSpeech = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(1);
  const [progress, setProgress] = useState(0);

  // Ref para controle de chunks
  const chunksRef = useRef([]);
  const currentChunkIndexRef = useRef(0);
  const utteranceRef = useRef(null);
  const fullTextRef = useRef('');
  const intervalRef = useRef(null);

  // Verificar suporte e carregar vozes
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      setIsSupported(true);

      const loadVoices = () => {
        const availableVoices = window.speechSynthesis.getVoices();
        if (availableVoices.length === 0) return;

        setVoices(prev => {
          if (prev.length === availableVoices.length &&
            prev[0]?.name === availableVoices[0]?.name) {
            return prev;
          }
          return availableVoices;
        });

        const ptBrVoice = availableVoices.find(
          (v) => v.lang === 'pt-BR' || v.lang.startsWith('pt')
        );

        const voiceToSelect = ptBrVoice || availableVoices[0];

        setSelectedVoice(current => {
          if (current && voiceToSelect && current.name === voiceToSelect.name) {
            return current;
          }
          return voiceToSelect;
        });
      };

      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;

      return () => {
        window.speechSynthesis.onvoiceschanged = null;
      };
    }
  }, []);

  // Limpar ao desmontar
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.speechSynthesis?.cancel();
    };
  }, []);

  // Função para dividir o texto em pedaços menores (chunks)
  const chunkText = (text) => {
    if (!text) return [];

    // Limpar o texto primeiro
    let cleanText = text.replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();

    // Dividir por sentenças (., !, ?) respeitando limites de ~200 caracteres para estabilidade
    const sentences = cleanText.split(/([.!?]+ +)/);
    const chunks = [];
    let currentChunk = "";

    for (let i = 0; i < sentences.length; i++) {
      const sentence = sentences[i];
      if ((currentChunk + sentence).length > 250 && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += sentence;
      }
    }

    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  };

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    if (intervalRef.current) clearInterval(intervalRef.current);
    chunksRef.current = [];
    currentChunkIndexRef.current = 0;
    setIsSpeaking(false);
    setIsPaused(false);
    setProgress(0);
  }, []);

  const speakChunk = useCallback((index) => {
    if (index >= chunksRef.current.length) {
      setIsSpeaking(false);
      setProgress(100);
      setTimeout(() => setProgress(0), 1000);
      return;
    }

    currentChunkIndexRef.current = index;
    const text = chunksRef.current[index];
    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    if (selectedVoice) utterance.voice = selectedVoice;
    utterance.rate = rate;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
      // Calcular progresso baseado no índice do chunk
      const baseProgress = (index / chunksRef.current.length) * 100;
      setProgress(baseProgress);
    };

    utterance.onend = () => {
      // Pequeno delay entre chunks para soar mais natural e evitar hits de API
      setTimeout(() => {
        if (!isPaused && chunksRef.current.length > 0) {
          speakChunk(index + 1);
        }
      }, 50);
    };

    utterance.onerror = (event) => {
      console.error('[TTS] Chunk Error:', event.error);
      if (event.error !== 'interrupted' && event.error !== 'canceled') {
        speakChunk(index + 1); // Tentar o próximo se falhar
      }
    };

    window.speechSynthesis.speak(utterance);
  }, [selectedVoice, rate, isPaused]);

  const speak = useCallback((text) => {
    if (!isSupported || !text) return;

    window.speechSynthesis.cancel();
    fullTextRef.current = text;
    const chunks = chunkText(text);
    chunksRef.current = chunks;
    currentChunkIndexRef.current = 0;

    if (chunks.length === 0) return;

    // Destravar motor se estiver pausado
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }

    speakChunk(0);
  }, [isSupported, speakChunk]);

  const pause = useCallback(() => {
    if (isSupported && isSpeaking) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, [isSupported, isSpeaking]);

  const resume = useCallback(() => {
    if (isSupported && isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      // Alguns navegadores 'perdem' o evento onend quando pausados, 
      // então reforçamos o status da síntese
      if (!window.speechSynthesis.speaking) {
        speakChunk(currentChunkIndexRef.current);
      }
    }
  }, [isSupported, isPaused, speakChunk]);

  const toggle = useCallback((text) => {
    if (isSpeaking && !isPaused) {
      pause();
    } else if (isPaused) {
      resume();
    } else {
      speak(text);
    }
  }, [isSpeaking, isPaused, pause, resume, speak]);

  const changeRate = useCallback((newRate) => {
    setRate(newRate);
    if (isSpeaking) {
      // Reiniciar a partir do chunk atual com a nova velocidade
      window.speechSynthesis.cancel();
      setTimeout(() => speakChunk(currentChunkIndexRef.current), 50);
    }
  }, [isSpeaking, speakChunk]);

  const changeVoice = useCallback((voice) => {
    setSelectedVoice(voice);
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setTimeout(() => speakChunk(currentChunkIndexRef.current), 50);
    }
  }, [isSpeaking, speakChunk]);

  return useMemo(() => ({
    isSupported,
    isSpeaking,
    isPaused,
    voices,
    selectedVoice,
    rate,
    progress,
    speak,
    pause,
    resume,
    stop,
    toggle,
    changeRate,
    changeVoice,
  }), [isSupported, isSpeaking, isPaused, voices, selectedVoice, rate, progress, speak, pause, resume, stop, toggle, changeRate, changeVoice]);
};

export default useTextToSpeech;
